import { useEffect, useId, useRef, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { cn } from "../lib/cn";
import { useDialogFocus } from "../hooks/useDialogFocus";
import { useModalPresence } from "../hooks/useModalPresence";

export interface ModalProps {
  /**
   * Render the dialog when true. Turning it false plays the close animation
   * and then unmounts the DOM subtree; the dialog is inert meanwhile.
   */
  open: boolean;
  /** Heading text; wired to `aria-labelledby`. */
  title: string;
  /** Hide the title visually; it stays the dialog's accessible name. */
  titleHidden?: boolean;
  /** Content centered on the X button's line, such as a stepper; pair it with `titleHidden`. */
  header?: ReactNode;
  /** Called by the X button and the Escape key (and a backdrop click when `closeOnBackdrop`). */
  onClose: () => void;
  /** Also dismiss on a click on the backdrop outside the panel. Default false (form modals). */
  closeOnBackdrop?: boolean;
  /**
   * Panel size: `md` (28rem wide, the default), `lg` (42rem wide) for
   * multi-column forms, or `xl` for multi-step flows. An `xl` panel is a 42rem
   * content column plus its padding wide, or the viewport less its 16px side
   * margins when narrower, and as tall as its content up to 80% of the
   * viewport, past which its header stays put and the body scrolls. Its height
   * follows the content with a 250ms ease-out transition, none under
   * "reduce motion".
   */
  size?: "md" | "lg" | "xl";
  children: ReactNode;
}

type ModalSize = NonNullable<ModalProps["size"]>;

/**
 * The `xl` panel's height is set in px from the measured content wrapper,
 * because a transition never starts on a `height: auto` box. The wrapper
 * keeps its natural height under the panel, so a growing step is clipped by
 * the panel while it animates rather than turning the body into a scroller.
 */
const PANEL_SIZE: Record<ModalSize, string> = {
  md: "max-w-md",
  lg: "max-w-2xl",
  xl: "max-w-[calc(var(--container-2xl)+3rem)] overflow-hidden p-0 transition-[height] duration-250 ease-out motion-reduce:transition-none",
};
const WRAPPER_SIZE: Partial<Record<ModalSize, string>> = {
  xl: "flex max-h-[80vh] flex-col p-6",
};
/**
 * The `xl` body is the scroll container; its 4px inset keeps the app-wide
 * `:focus-visible` outline (2px plus a 2px offset) of a control at its edge
 * from being clipped.
 */
const BODY_SIZE: Partial<Record<ModalSize, string>> = {
  xl: "-mx-1 mt-2 min-h-0 overflow-y-auto p-1",
};

/**
 * Portal-mounted dialog with a title, an X button and Escape to close.
 *
 * It portals to `document.body`, locks page scroll while open, and sets
 * `role="dialog"`, `aria-modal` and `aria-labelledby`. A backdrop click doesn't
 * dismiss by default, because in a form modal a stray click would wipe
 * half-typed input; read-only modals opt in with `closeOnBackdrop`. Callers
 * keep their own actions in `children`.
 *
 * Focus and keys come from `useDialogFocus`: opening moves focus to the first
 * control in `children` (so a form starts on its first field and a confirm
 * dialog on its first button), else the X button, else the panel itself; Tab
 * and Shift+Tab cycle within the panel; Escape closes; closing returns focus
 * to the element that was focused before the dialog opened, when it is still
 * in the document and not inside a closing dialog. With dialogs stacked, a
 * Modal or any other `useDialogFocus` dialog, only the most recently opened
 * one handles Escape and the Tab wrap. The panel scrolls inside the viewport
 * when its content is taller, and the app-wide `:focus-visible` outline marks
 * the panel when it takes focus itself.
 *
 * Motion: opening fades the backdrop in and fades and scales the panel up.
 * Closing plays the reverse through `useModalPresence` before the subtree
 * unmounts. Under "reduce motion" both are instant. A closing Modal is `inert`
 * and `aria-hidden` at once: it has already left the stack, ignores keys and
 * clicks, and has returned focus.
 */
export function Modal({
  open,
  title,
  titleHidden = false,
  header,
  onClose,
  closeOnBackdrop = false,
  size = "md",
  children,
}: ModalProps): ReactNode {
  const titleId = useId();
  const panelRef = useRef<HTMLDivElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const bodyRef = useRef<HTMLDivElement>(null);
  const [height, setHeight] = useState<number | null>(null);
  const { mounted, onAnimationEnd } = useModalPresence(open);
  useDialogFocus({ open, panelRef, initialRef: bodyRef, onClose });

  // Size the `xl` panel to its content wrapper; the panel opens at its
  // natural height, so nothing animates on open.
  useEffect(() => {
    const wrapper = wrapperRef.current;
    if (!open || size !== "xl" || wrapper === null) return;
    const observer = new ResizeObserver(([entry]) => {
      const box = entry?.borderBoxSize[0];
      if (box !== undefined) setHeight(box.blockSize);
    });
    observer.observe(wrapper);
    return () => {
      observer.disconnect();
      setHeight(null);
    };
  }, [open, size]);

  if (!mounted) return null;

  const close = (): void => {
    if (open) onClose();
  };

  const node = (
    <div
      className={cn(
        "bg-midnight/30 fixed inset-0 z-50 flex items-center justify-center px-4 py-6",
        open ? "motion-safe:animate-modal-backdrop-in" : "motion-safe:animate-modal-backdrop-out",
      )}
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
      inert={!open}
      aria-hidden={open ? undefined : true}
      // Only a click on the backdrop itself closes; one bubbling up from the panel keeps the dialog open.
      onClick={
        closeOnBackdrop
          ? (e) => {
              if (e.target === e.currentTarget) close();
            }
          : undefined
      }
    >
      <div
        ref={panelRef}
        tabIndex={-1}
        className={cn(
          "max-h-full w-full overflow-y-auto rounded-3xl bg-white p-6 shadow-xl",
          open ? "motion-safe:animate-modal-panel-in" : "motion-safe:animate-modal-panel-out",
          PANEL_SIZE[size],
        )}
        style={height === null ? undefined : { height }}
        onAnimationEnd={onAnimationEnd}
      >
        <div ref={wrapperRef} className={WRAPPER_SIZE[size]}>
          <div className="relative flex items-start justify-between gap-4">
            <h2
              id={titleId}
              className={cn(
                "text-foreground font-serif text-xl font-normal leading-[1.5]",
                titleHidden && "sr-only",
              )}
            >
              {title}
            </h2>
            {header !== undefined && (
              // Spans the X button's 32px box, inset past it on both sides so it stays centered.
              <div className="absolute inset-x-10 -top-1 flex h-8 items-center justify-center">
                {header}
              </div>
            )}
            <button
              type="button"
              aria-label="Close"
              onClick={close}
              className="text-slate-teal hover:bg-ice hover:text-midnight -mr-1 -mt-1 ml-auto inline-flex size-8 shrink-0 cursor-pointer items-center justify-center rounded-full"
            >
              <svg
                width={18}
                height={18}
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth={1.75}
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </div>
          <div ref={bodyRef} className={cn("mt-3", BODY_SIZE[size])}>
            {children}
          </div>
        </div>
      </div>
    </div>
  );

  return createPortal(node, document.body);
}
